import type { ActivityOf } from '@wyn/content';
import { createRng } from '../rng.ts';
import { DT, type RealtimeSim, TICK_HZ } from './realtime.ts';
import { clamp, type Fx, lerp, type MinigameResult, plural } from './types.ts';

/** Past this much wind-up the woodcutter overbalances. */
export const OVERSWING = 100;
/** The red cap on the gauge starts here. */
export const OVERSWING_WARN = 95;
const MAX_SECONDS = 180;

export type AxeState = 'ready' | 'charge' | 'swing' | 'stuck' | 'stagger' | 'recover';

export interface WoodcuttingSim extends RealtimeSim {
  state: AxeState;
  /** Seconds in the current state. */
  stateTime: number;
  /** Wind-up, 0–100. */
  power: number;
  /** The gold band, in wind-up percent. */
  sweet: [number, number];
  hp: number;
  chops: number;
  maxChops: number;
  streak: number;
  best: number;
  felled: boolean;
}

/**
 * The Logging Camp. Hold to wind the axe back along its arc and release in the gold band. Clean cuts
 * in a row build a streak (deeper chops, more XP, bonus logs). Ported from the Harrowmere prototype.
 */
export function woodcuttingSim(a: ActivityOf<'woodcutting'>, level: number, seed: string): WoodcuttingSim {
  const rng = createRng(seed);
  const rand = (lo: number, hi: number) => lo + rng.next() * (hi - lo);
  const bandW = Math.min(a.band + Math.max(0, level - a.level) * 0.35, a.band * 1.6);
  const newSweet = (): [number, number] => {
    const s = rand(55, 94 - bandW);
    return [s, s + bandW];
  };

  // After an overswing the woodcutter must let go before winding up again.
  let mustRelease = false;
  let pending: { d: number; label: string; tone: Fx['tone']; clean: boolean } | null = null;

  const fx = (text: string, tone: Fx['tone'], cue?: string, power?: number) => sim.fx.push({ tick: sim.tick, text, tone, cue, power });
  const end = (r: MinigameResult) => void (sim.result = r);
  const cut = () => clamp(1 - sim.hp / a.hp, 0, 1);

  const spent = () => {
    fx('You are spent', 'bad', 'spent');
    const logs = sim.hp < a.hp / 2 ? 1 : 0;
    end({
      score: 0.2 * cut(),
      title: 'Out of breath',
      text: 'The tree still stands, so you earn no experience. You gather a few fallen limbs and trudge home.',
      breakdown: [['Tree cut', cut()]],
      yield: logs ? { itemKey: a.log, quantity: logs, graded: false } : null,
      coin: 0,
      xpMult: 0,
      feats: ['spent'],
    });
  };

  const impact = () => {
    const { d, label, tone, clean } = pending!;
    pending = null;
    if (clean) {
      sim.streak++;
      sim.best = Math.max(sim.best, sim.streak);
    } else sim.streak = 0;
    sim.hp -= clean ? d * (1 + Math.min(sim.streak - 1, 4) * 0.1) : d;
    sim.chops++;
    fx(clean && sim.streak > 1 ? `${label} 🔥×${sim.streak}` : label, tone, 'chop', d);
    sim.sweet = newSweet();
    if (sim.hp <= 0.001) {
      sim.felled = true;
      fx('Timber!', 'good', 'felled');
      const score = clamp(a.hp / sim.chops, 0, 1);
      const bonus = Math.floor(sim.best / 3);
      const logs = Math.round(lerp(a.logs[0], a.logs[1], score)) + bonus;
      const xpMult = Math.min(1.5, 1 + Math.max(0, sim.best - 1) * 0.1);
      const streakNote = sim.best > 1 ? ` Best streak ×${sim.best}: +${Math.round((xpMult - 1) * 100)}% XP${bonus ? `, +${plural(bonus, 'bonus log')}` : ''}.` : '';
      end({
        score,
        title: 'The tree comes down',
        text: `You fell the ${a.name.toLowerCase()} in ${plural(sim.chops, 'chop')}.${streakNote}`,
        breakdown: [['Efficiency', score], ['Best streak', Math.min(1, sim.best / 5)]],
        yield: { itemKey: a.log, quantity: logs, graded: false },
        coin: 0,
        xpMult,
        feats: sim.best >= 5 ? ['streak-five'] : [],
      });
    } else if (sim.chops >= sim.maxChops) spent();
  };

  const go = (s: AxeState) => {
    sim.state = s;
    sim.stateTime = 0;
  };

  const sim: WoodcuttingSim = {
    tick: 0,
    result: null,
    fx: [],
    state: 'ready',
    stateTime: 0,
    power: 0,
    sweet: newSweet(),
    hp: a.hp,
    chops: 0,
    maxChops: Math.ceil(a.hp * 1.8) + Math.floor(level / 5),
    streak: 0,
    best: 0,
    felled: false,
    step(mask) {
      if (sim.result) return;
      sim.tick++;
      if (!(mask & 1)) mustRelease = false;
      const holding = (mask & 1) === 1 && !mustRelease;
      sim.stateTime += DT;
      switch (sim.state) {
        case 'ready':
          if (holding) {
            go('charge');
            sim.power = 0;
          }
          break;
        case 'charge':
          if (holding) {
            sim.power += 95 * a.rate * DT;
            if (sim.power >= OVERSWING) {
              sim.chops++;
              sim.power = 0;
              sim.streak = 0;
              mustRelease = true;
              go('stagger');
              fx('Overswing! You stagger', 'bad', 'overswing');
            }
          } else {
            const p = Math.min(99.9, sim.power);
            sim.power = 0;
            const [lo, hi] = sim.sweet;
            pending =
              p >= lo && p <= hi ? { d: 1, label: 'Clean cut!', tone: 'good', clean: true }
              : p > hi ? { d: 0.6, label: 'Heavy', tone: 'gold', clean: false }
              : { d: clamp((p / lo) * 0.5, 0.05, 0.5), label: 'Weak', tone: 'muted', clean: false };
            go('swing');
          }
          break;
        case 'swing':
          if (sim.stateTime >= 0.13) {
            impact();
            go('stuck');
          }
          break;
        case 'stuck':
          if (sim.stateTime > 0.18) go('recover');
          break;
        case 'stagger':
          if (sim.stateTime >= 0.7) {
            go('recover');
            if (sim.chops >= sim.maxChops) spent();
          }
          break;
        case 'recover':
          if (sim.stateTime > 0.3) go('ready');
          break;
      }
      if (!sim.result && sim.tick >= MAX_SECONDS * TICK_HZ) spent();
    },
  };
  return sim;
}
